<template lang="pug">
  .type-intro(:class="color")
    .doc-image
      img(:src="doc")
    vue-custom-scrollbar.description(:settings="settings")
      .text
        h2.f-0 Redaction —
        h2.f-0 a multiplicity of typographic,
        h2.f-0 legal, and human histories
      .description-sub
        p.f-0 Redaction is a bespoke typeface commissioned by <a target="_blank" href="https://kapharstudio.com/">Titus Kaphar</a> and <a target="_blank" href="http://www.dwaynebetts.com/">Reginald Dwayne Betts</a>’ <i>The Redaction</i> exhibition at <a target="_blank" href="http://www.dwaynebetts.com/">MoMA PS1</a>. As part of a timely conversation at the intersection of history, the legal system and social justice, the fonts will be free for personal use. In a spirit of generosity, the artists invite everyone to broaden the ethos of the exhibition by making these tools accessible to a global and engaged audience.
      .essay
        p.f-0 The Redaction project seeks to highlight the abuses in the criminal justice system, in particular the way poor and marginalized people are imprisoned for failure to pay court fines and fees. When Titus Kaphar and Reginald Dwayne Betts began their collaboration on The Redaction, they identified that typography should be an important extension of the work. Using legal documents from claims filed by the Civil Rights Corps as source material, Betts crafted new poems by redacting out superfluous information – text that is juxtaposed with Kaphar’s portraits of the individuals represented in the claims. This emphasis on text and legibility presented a unique design opportunity: to create a bespoke typeface that could be used in the work, and also serve as a scalable tool to raise awareness of the project and reach even more people. To fulfill the typographic potential of the project, Kaphar reached out to <a target="_blank" href="https://www.moma.org/interactives/exhibitions/2011/talktome/objects/140027/">Forest Young</a>, a colleague from Yale, now a Global Principal and Head of Design at Wolff Olins, who in turn recruited Jeremy Mickel, type designer and owner of <a target="_blank" href="https://mckltype.com/">MCKL</a>.
        .images
          figure(v-for="i in redactionImages")
            img(:src="i.src")
            figcaption {{ i.cap }}
        p.f-0 The first phase of the project consisted of research and immersion. It was imperative to learn as much as possible about the history and nature of legal documents, and the various approaches to redaction. Unsurprisingly, the designers found a spectrum of redaction: forms extending beyond the simple black box — an incomplete redaction; stamps marked ‘deleted’ but not obscuring the text; white boxes that appeared to invite the user to fill in their own text, and ad hoc handmade notations, among others.
        p.f-0 Specifically, the team noted that the vast majority of legal documents  in the United States followed a set convention of using Times New Roman. The United States Supreme Court, however, dictates that all documents must be set in <a target="_blank" href="https://www.law.cornell.edu/rules/supct/rule_33">New Century Schoolbook</a>. Both typefaces felt default, functional, and familiar. While they do not call attention to themselves at small size, they still command a sense of authority — attributes which the team hoped to emulate in their own design.
        .images
          figure(v-for="i in degrationImages")
            img(:src="i.src")
            figcaption {{ i.cap }}
        p.f-0 In reviewing historical source material, the team observed varying states of typographic degradation in the text of countless documents. As files were faxed, photocopied, and otherwise reproduced, the letterforms became bitmapped, bloated, warped, and were sometimes reduced to near-illegible forms. This particular aspect of variable legibility inspired subsequent experiments for future iterations of the fonts.
        p.f-0 Times New Roman and New Century Schoolbook became the foundational pillars of legal typographic influence. The first goal was to draft something which simultaneously embodied both typefaces. While early tests of hybridization proved successful, the letterforms lacked a desired immediacy and contrast that could elevate them to both the abstract and conceptual, and ultimately be worthy of being incorporated into Kaphar and Betts’ work.
        .image
          img(src="/static/essay_images/eagle.svg")
        p.f-0 An early point of inspiration was the United States presidential seal, which depicts an eagle grasping arrows in one talon and an olive branch in the other. This was embraced as a potent metaphor for the extremes in the legal system: cruelty and compassion, war and diplomacy, hard and soft, white and black. This logic was then applied to the letterforms: teardrop shapes became supple and round, while acute shapes became razor sharp. When this exaggerated contrast resulted in an amplified tension, the team knew they were on the right track.
        .image
          img(src="/static/essay_images/henis.svg")
        p.f-0 Pushing ahead, the team looked back at the early bitmap studies and document references and wondered if they could synthesize those details into the the font. By incorporating negative shapes into the letterforms, the. typeface could hint at the digital artifacts created through reproduction in fax machines and photocopiers. Peter Saville’s <a target="_blank" href="https://en.wikipedia.org/wiki/Power,_Corruption_%26_Lies">Power, Corruption, and Lies</a> album cover for New Order served as additional inspiration; the unassuming color bar on an otherwise mundane floral still life effortlessly merges contemporary and historical narratives. A pixel fit nicely into the joins on characters like H E, but required bending and shaping to be convincing on the n s. Through trial and error, the letterform design followed an “inktrap logic” — to place negative shapes into as many of the glyphs as possible, with the additional pixel detail of a square tittle for the i dot and period.
        .image
          img(src="/static/essay_images/r.svg")
        p.f-0 It was always the hope to include bitmap grades of the font in order to reference the degradation of documents as they are reproduced through the legal process. As contemporary design moves through a zeitgeist of pixelation, with <a target="_blank" href="https://www.instagram.com/p/BvBxwDPAerD/?utm_source=ig_web_button_share_sheet">many</a>&#32<a target="_blank" href="https://www.instagram.com/p/BtW2S_WBgu7/?utm_source=ig_web_button_share_sheet">incredible</a>&#32<a target="_blank" href="https://www.instagram.com/p/ButFvWnFI3I/?utm_source=ig_web_button_share_sheet">examples</a>&#32<a target="_blank" href="https://www.instagram.com/p/Bu_huDiggAZ/?utm_source=ig_web_button_share_sheet">online</a>, the team recalled the landmark designs by <a target="_blank" href="https://en.wikipedia.org/wiki/Susan_Kare">Susan Kare</a> for the original Apple Operating System and many of <a target="_blank" href="https://www.emigre.com/Fonts/Lo-Res/">Zuzana Licko’s</a> early digital fonts for Emigre. The team maintained a conceptual framework for the degraded versions; it’s decidedly not merely an aesthetic. By providing a range of grades from subtly analog to nearly illegible, the typeface nods to the transformation and marginalization that many people face in the criminal justice system today, and specifically, the role and responsibility of the author of text to be conscious of legibility as a signature of power.
        .image
          img(src="/static/essay_images/redaction.svg")
        p.f-0 As part of the artists’ dedication to social justice and legal equity, the Redaction fonts are distributed under the <a target="_blank" href="https://scripts.sil.org/cms/scripts/page.php?site_id=nrsi&id=OFL">Open Font License.</a> You can use them freely in your products and projects – print or digital, commercial or otherwise. However, you can't sell the fonts on their own. The fonts are available in 3 styles (Regular, Italic, Bold) and 7 grades of degradation for a total of 21 fonts. All fonts have full character sets for Latin-1 language support.
        p.f-0 The team is particularly excited to see how the Redaction fonts get used, and will showcase examples on the site in the future. Please keep in touch and share any examples of the fonts in use by contacting us at <a target="_blank" href="mailto:info@redaction.us">info@redaction.us</a>.
        p.f-0 Special thanks to Titus and Dwayne for extending the invitation to participate in this noble work. It is the team's hope that you will enjoy using Redaction.
</template>

<script>
import vueCustomScrollbar from 'vue-custom-scrollbar'

export default {
  name: "FontIntro",

  props: ["color"],

  components: {
    vueCustomScrollbar
  },

  data() {
    return {
      redactionImages: [
        {
          src: '/static/essay_images/redact-1.jpg',
          cap: 'Highlighter emphasizes text'
        },
        {
          src: '/static/essay_images/redact-2.jpg',
          cap: 'All text treated equally'
        },
        {
          src: '/static/essay_images/redact-3.jpg',
          cap: 'Strikethrough deemphasizes text, but still legible'
        },
        {
          src: '/static/essay_images/redact-4.jpg',
          cap: 'Text redacted, but still possible to read'
        },
        {
          src: '/static/essay_images/redact-5.jpg',
          cap: 'Complete redaction of text'
        }
      ],
      degrationImages: [
        {
          src: '/static/essay_images/degrade-1.jpg',
          cap: 'Original source'
        },
        {
          src: '/static/essay_images/degrade-2.jpg',
          cap: 'Bitmap from fax'
        },
        {
          src: '/static/essay_images/degrade-3.jpg',
          cap: 'Bulking / distortion from photocopying'
        },
        {
          src: '/static/essay_images/degrade-4.jpg',
          cap: 'Degradation from photographing faint historical material'
        },
        {
          src: '/static/essay_images/degrade-5.jpg',
          cap: 'Coarse bitmap from high-speeding scanning'
        }
      ],
      settings: {
        // scrollYMarginOffset: -50
      }
    };
  },

  computed: {
    doc() {
      return require("@/assets/images/doc-outline.svg");
    }
  }
};
</script>

<style lang="sass">
@import '@/styles/_variables.sass'
.type-intro
  width: 100vw
  height: 130vh
  position: relative
  display: none
  @media only screen and (min-width: $m)
    display: block
  .doc-image
    width: 88.19vw
    height: 45vw
    position: absolute
    left: 50%
    top: 50%
    transform: translate(-50%, -50%)
    img
      width: 100%
  .description
    width: 77vw
    height: 45vw
    overflow: hidden
    position: absolute
    top: 50%
    left: 6.94444vw
    transform: translate(0, -50%)
    .ps__rail-y
      width: 5rem !important
      background-color: #DCDDDE
      opacity: 1
      margin-top: 17vw
      height: 25vw !important
    &:hover
      &>.ps__thumb-y
        width: calc(5rem - 4px)
    .ps__thumb-y
      width: calc(5rem - 4px)
      height: calc(5rem - 4px) !important
      background-color: #FFF
      border: 2px solid #000
      border-radius: 0
      right: 0 !important
    .text
      color: #000
      margin-top: 6.94444vw
      margin-left: 6.94444vw
      z-index: 9
      h2
        font-size: 4.5rem
        line-height: 5.5rem
        &:nth-child(1)
          font-size: 9rem
          line-height: 9.1rem
    .description-sub
      color: #000
      margin-left: 6.94444vw
      margin-top: 14rem
      z-index: 9
      width: 58.68055556vw
      p
        font-size: 1.8rem
        line-height: 2.5rem
    .essay
      margin-left: 6.94444vw
      width: 58.68055556vw
      margin-top: 2rem
      margin-bottom: 10rem
      p
        color: #000
        font-size: 1.8rem
        line-height: 2.5rem
        margin-bottom: 1.8rem
      .image
        width: 100%
        margin: 6rem 0 4rem 0
      .images
        display: flex
        justify-content: space-between
        margin: 6rem 0 4rem 0
        figure
          width: 19%
          height: 19%
          margin: 0
          img
            width: 100%
            height: 100%
          figcaption
            margin-top: 1rem
            color: #000
            text-align: left
            font-family: "Courier New", Courier, monospace
  a
    text-decoration: underline
</style>
